'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { delay } from 'es-toolkit';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useRankingList } from './_hooks/useRankingList';
type TabType = 'reward' | 'ranking';
type TabConfig = {
  id: TabType;
  sectionId: string;
  label: string;
  subLabel: string;
};
const TAB_HEIGHT = 68;
const TABS: TabConfig[] = [{
  id: 'reward',
  sectionId: 'reward-section',
  label: '+BIG 선물',
  subLabel: '무제한 현금'
}, {
  id: 'ranking',
  sectionId: 'ranking-section',
  label: '랭킹',
  subLabel: '친구 초대'
}];
const useActiveTab = () => {
  const $ = _c(5);
  const [activeTab, setActiveTab] = useState("reward");
  const isClickingRef = useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const hash = window.location.hash.replace("#", "");
      const initialTab = TABS.find(tab => tab.sectionId === hash);
      if (initialTab) {
        setActiveTab(initialTab.id);
      }
      const observer = new IntersectionObserver(entries => {
        if (isClickingRef.current) {
          return;
        }
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const tab_0 = TABS.find(t => t.sectionId === entry.target.id);
            if (tab_0) {
              setActiveTab(tab_0.id);
            }
          }
        }
      }, {
        rootMargin: `-${TAB_HEIGHT}px 0px -50% 0px`
      });
      TABS.forEach(t2 => {
        const {
          sectionId
        } = t2;
        const el = document.getElementById(sectionId);
        if (el) {
          observer.observe(el);
        }
      });
      return () => observer.disconnect();
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = async tabId => {
      setActiveTab(tabId);
      isClickingRef.current = true;
      await delay(1000);
      isClickingRef.current = false;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleTabClick = t2;
  let t3;
  if ($[3] !== activeTab) {
    t3 = {
      activeTab,
      handleTabClick
    };
    $[3] = activeTab;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const TabItem = t0 => {
  const $ = _c(21);
  const {
    tab,
    isActive,
    onClick
  } = t0;
  const t1 = `#${tab.sectionId}`;
  const t2 = tab.sectionId;
  const t3 = isActive ? 0 : -1;
  const t4 = isActive ? "border-[#E4302C]" : "border-Gray-300";
  let t5;
  if ($[0] !== t4) {
    t5 = cn("flex h-68 flex-1 flex-col items-center justify-center border-b-[3px] outline-none transition-all", t4);
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const t6 = isActive ? "text-Gray-black" : "text-Gray-500";
  let t7;
  if ($[2] !== t6) {
    t7 = cn("font-700 typo-body4", t6);
    $[2] = t6;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== t7 || $[5] !== tab.subLabel) {
    t8 = <span className={t7}>{tab.subLabel}</span>;
    $[4] = t7;
    $[5] = tab.subLabel;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const t9 = isActive ? "text-Gray-black" : "text-Gray-500";
  let t10;
  if ($[7] !== t9) {
    t10 = cn("font-700 typo-body4", t9);
    $[7] = t9;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] !== t10 || $[10] !== tab.label) {
    t11 = <span className={t10}>{tab.label}</span>;
    $[9] = t10;
    $[10] = tab.label;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== isActive || $[13] !== onClick || $[14] !== t1 || $[15] !== t11 || $[16] !== t3 || $[17] !== t5 || $[18] !== t8 || $[19] !== tab.sectionId) {
    t12 = <Link href={t1} replace={true} onClick={onClick} role="tab" aria-selected={isActive} aria-controls={t2} tabIndex={t3} className={t5}>{t8}{t11}</Link>;
    $[12] = isActive;
    $[13] = onClick;
    $[14] = t1;
    $[15] = t11;
    $[16] = t3;
    $[17] = t5;
    $[18] = t8;
    $[19] = tab.sectionId;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
};
export const InviteTab = () => {
  const $ = _c(5);
  const {
    activeTab,
    handleTabClick
  } = useActiveTab();
  const {
    data: rankingData
  } = useRankingList();
  if (rankingData && rankingData.length === 0) {
    return null;
  }
  let t0;
  if ($[0] !== activeTab || $[1] !== handleTabClick) {
    t0 = TABS.map(tab => <TabItem key={tab.id} tab={tab} isActive={activeTab === tab.id} onClick={() => handleTabClick(tab.id)} />);
    $[0] = activeTab;
    $[1] = handleTabClick;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = <div className="sticky top-0 z-[2] flex w-full bg-Gray-white" role="tablist" aria-label={"\uCE5C\uAD6C \uCD08\uB300 \uC774\uBCA4\uD2B8 \uB124\uBE44\uAC8C\uC774\uC158"}><div className="mx-auto flex w-full max-w-[750px]">{t0}</div></div>;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};